import { useState, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { downloadData } from 'aws-amplify/storage';
import { sendEmail } from '../services/MessagesService';
import { useAliases } from '../../aliases/hooks/useAliases';
import { htmlToPlainText } from '../../../utils/htmlSanitizer';
import { Message } from '../types';
import { useGenerateTestMessage } from './useGenerateTestMessage';

interface ComposeEmailFormValues {
    aliasId: string;
    to: string;
    subject: string;
    htmlBody: string;
    useHtml: boolean;
}

export const useComposeEmail = (
    opened: boolean,
    aliasId?: string,
    replyTo?: Message,
    replyAll?: boolean,
    forwardMessage?: Message
) => {
    const { items: aliases } = useAliases();
    const queryClient = useQueryClient();
    const { generateTestMessage } = useGenerateTestMessage();
    const [initialValues, setInitialValues] = useState<ComposeEmailFormValues>({
        aliasId: aliasId || '',
        to: '',
        subject: '',
        htmlBody: '',
        useHtml: true
    });

    const aliasOptions = aliases.map((alias) => ({
        value: alias.id,
        label: alias.name || alias.id,
    }));

    const getMessageTextPath = (message: Message): string | null => {
        let textPath = message.textObjectKey || message.textPath || message.textAsHtmlPath;
        if (!textPath && message.htmlObjectKey) {
            textPath = message.htmlObjectKey.replace('/html', '/text');
        }
        return textPath || null;
    };

    const loadMessageText = async (path: string): Promise<string> => {
        const { body } = await downloadData({
            path: path as string,
            options: {}
        }).result;
        return await body.text();
    };

    const formatForwardedMessage = (message: Message, text: string): string => {
        return `<p><br></p><hr><p><strong>Forwarded message:</strong></p><p><strong>From:</strong> ${message.fromText || ''}</p><p><strong>Date:</strong> ${message.date || ''}</p><p><strong>Subject:</strong> ${message.subject || ''}</p><p><strong>To:</strong> ${message.toText || ''}</p><p><br></p><p>${text.replace(/\n/g, '<br>')}</p>`;
    };

    const formatReplyMessage = (message: Message, text: string): string => {
        return `<p><br></p><hr><p>On ${message.date || ''}, ${message.fromText || ''} wrote:</p><blockquote>${text.replace(/\n/g, '<br>')}</blockquote>`;
    };

    useEffect(() => {
        if (!opened || (!replyTo && !forwardMessage)) {
            setInitialValues({ aliasId: aliasId || '', to: '', subject: '', htmlBody: '', useHtml: true });
            return;
        }

        const loadMessage = async () => {
            const message = replyTo || forwardMessage;
            if (!message) return;

            if (forwardMessage) {
                const subject = forwardMessage.subject?.startsWith('Fwd: ') 
                    ? forwardMessage.subject 
                    : `Fwd: ${forwardMessage.subject || ''}`;
                
                const textPath = getMessageTextPath(forwardMessage);
                
                if (textPath) {
                    try {
                        const text = await loadMessageText(textPath);
                        const forwardedBody = formatForwardedMessage(forwardMessage, text);
                        
                        setInitialValues({
                            aliasId: forwardMessage.aliasId || aliasId || '',
                            to: '',
                            subject,
                            htmlBody: forwardedBody,
                            useHtml: true,
                        });
                    } catch (err) {
                        console.error('Failed to load message text:', err);
                        setInitialValues({
                            aliasId: forwardMessage.aliasId || aliasId || '',
                            to: '',
                            subject,
                            htmlBody: '',
                            useHtml: true,
                        });
                    }
                } else {
                    setInitialValues({
                        aliasId: forwardMessage.aliasId || aliasId || '',
                        to: '',
                        subject,
                        htmlBody: '',
                        useHtml: true,
                    });
                }
            } else if (replyTo) {
                const toRecipients = replyAll && replyTo.toText ? replyTo.toText : replyTo.fromText || '';
                const subject = replyTo.subject?.startsWith('Re: ') 
                    ? replyTo.subject 
                    : `Re: ${replyTo.subject || ''}`;
                
                const textPath = getMessageTextPath(replyTo);
                
                if (textPath) {
                    try {
                        const text = await loadMessageText(textPath);
                        const quotedBody = formatReplyMessage(replyTo, text);
                        
                        setInitialValues({
                            aliasId: replyTo.aliasId || aliasId || '',
                            to: toRecipients,
                            subject,
                            htmlBody: quotedBody,
                            useHtml: true,
                        });
                    } catch (err) {
                        console.error('Failed to load message text:', err);
                        setInitialValues({
                            aliasId: replyTo.aliasId || aliasId || '',
                            to: toRecipients,
                            subject,
                            htmlBody: '',
                            useHtml: true,
                        });
                    }
                } else {
                    setInitialValues({
                        aliasId: replyTo.aliasId || aliasId || '',
                        to: toRecipients,
                        subject,
                        htmlBody: '',
                        useHtml: true,
                    });
                }
            }
        };

        loadMessage();
    }, [opened, replyTo, replyAll, forwardMessage, aliasId]);

    const handleSubmit = async (values: ComposeEmailFormValues) => {
        const recipients = values.to.split(',').map(email => email.trim());
        
        await sendEmail({
            aliasId: values.aliasId,
            to: recipients,
            subject: values.subject,
            htmlBody: values.useHtml ? values.htmlBody : undefined,
            textBody: values.useHtml ? htmlToPlainText(values.htmlBody) : values.htmlBody,
            inReplyTo: replyTo?.messageId,
        });
        
        queryClient.invalidateQueries({ queryKey: ['messages'] });
    };

    const getTitle = () => {
        if (forwardMessage) return 'Forward';
        if (replyTo) return replyAll ? 'Reply All' : 'Reply';
        return 'Compose Email';
    };

    return {
        initialValues,
        aliasOptions,
        handleSubmit,
        generateTestMessage,
        title: getTitle(),
    };
};
